/** @jsxImportSource @emotion/react */
import React from "react";
import { css } from "@emotion/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Header, flexRender } from "@tanstack/react-table";
import { Icon, Icons, Text } from "@phoenix/components";
import { Truncate } from "@phoenix/components/core/utility/Truncate";

interface DraggableTableHeaderProps<T> {
    header: Header<T, unknown>;
}

export function DraggableTableHeader<T>({ header }: DraggableTableHeaderProps<T>) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: header.id });

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
        width: `calc(var(--header-${header.id}-size) * 1px)`,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
        position: "relative",
        cursor: "default",
    };

    return (
        <th
            colSpan={header.colSpan}
            ref={setNodeRef}
            style={style}
        >
            {header.isPlaceholder ? null : (
                <div
                    css={css`
            display: flex;
            align-items: center;
            gap: 4px;
            width: 100%;
            height: 100%;
          `}
                >
                    <div
                        {...attributes}
                        {...listeners}
                        css={css`
              cursor: grab;
              display: flex;
              align-items: center;
              opacity: 0.35;
              color: #475569;
              &:hover {
                opacity: 0.9;
                color: #0f172a;
              }
              .theme--dark & {
                color: #94a3b8;
                &:hover {
                  color: #f1f5f9;
                }
              }
            `}
                    >
                        <Icon
                            svg={
                                <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                                    <circle cx="2.5" cy="2.5" r="1.5" />
                                    <circle cx="2.5" cy="7" r="1.5" />
                                    <circle cx="2.5" cy="11.5" r="1.5" />
                                    <circle cx="7.5" cy="2.5" r="1.5" />
                                    <circle cx="7.5" cy="7" r="1.5" />
                                    <circle cx="7.5" cy="11.5" r="1.5" />
                                </svg>
                            }
                            css={css`width: 10px; height: 14px;`}
                        />
                    </div>

                    <div
                        {...{
                            className: header.column.getCanSort() ? "sort" : "",
                            onClick: header.column.getToggleSortingHandler(),
                            css: css`
                display: flex;
                align-items: center;
                gap: 4px;
                flex: 1;
                overflow: hidden;
                cursor: pointer;
                font-size: 15px !important;
                font-weight: 750 !important;
                color: #0f172a !important;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif !important;
                .theme--dark & {
                  color: #f8fafc !important;
                }
              `,
                        }}
                    >
                        <Truncate maxWidth="100%">
                            {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                            )}
                        </Truncate>
                        {header.column.getIsSorted() ? (
                            <Icon
                                className="sort-icon"
                                svg={
                                    header.column.getIsSorted() === "asc" ? (
                                        <Icons.ArrowUpFilled />
                                    ) : (
                                        <Icons.ArrowDownFilled />
                                    )
                                }
                            />
                        ) : null}
                    </div>

                    <div
                        {...{
                            onMouseDown: header.getResizeHandler(),
                            onTouchStart: header.getResizeHandler(),
                            className: `resizer ${header.column.getIsResizing() ? "isResizing" : ""}`,
                        }}
                        css={css`
              position: absolute;
              right: 0;
              top: 0;
              height: 100%;
              width: 5px;
              background: rgba(0, 0, 0, 0.1);
              cursor: col-resize;
              user-select: none;
              touch-action: none;
              opacity: 0;
              &:hover, &.isResizing {
                opacity: 1;
                background: var(--ac-global-color-blue-500);
              }
            `}
                    />
                </div>
            )}
        </th>
    );
}
